import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { openApi } from "../../services/api";

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    background: "linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%)"
  },
  card: {
    width: "100%",
    maxWidth: 620,
    padding: theme.spacing(4),
    borderRadius: 18,
    boxShadow: "0 24px 60px rgba(15,23,42,0.14)"
  },
  title: {
    fontWeight: 800
  },
  statusWrap: {
    textAlign: "center"
  },
  statusIcon: {
    fontSize: 56,
    lineHeight: 1,
    marginBottom: theme.spacing(1)
  },
  stars: {
    display: "flex",
    gap: 12,
    justifyContent: "center",
    margin: theme.spacing(3, 0)
  },
  starButton: {
    fontSize: 34,
    background: "transparent",
    border: "none",
    cursor: "pointer"
  }
}));

export default function ServiceRating() {
  const classes = useStyles();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payload, setPayload] = useState(null);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [viewState, setViewState] = useState("form");

  useEffect(() => {
    if (viewState !== "submitted" && viewState !== "used") return undefined;

    const keepHere = () => {
      window.history.pushState(null, "", window.location.href);
    };

    keepHere();
    window.addEventListener("popstate", keepHere);
    return () => window.removeEventListener("popstate", keepHere);
  }, [viewState]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await openApi.get(`/service-rating/${token}`);
        setPayload(data);
        if (data?.alreadySubmitted) {
          setViewState("used");
        } else if (data?.existingRating) {
          setStars(Number(data.existingRating.stars || 0));
          setComment(data.existingRating.comment || "");
        }
      } catch (_error) {
        setPayload({ valid: false });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await openApi.post(`/service-rating/${token}`, { stars, comment });
      setSubmitted(true);
      setViewState("submitted");
    } catch (error) {
      if (error?.response?.status === 409) {
        setViewState("used");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <CircularProgress />
      </div>
    );
  }

  if (!payload?.valid) {
    return (
      <div className={classes.root}>
        <Paper className={classes.card}>
          <div className={classes.statusWrap}>
            <div className={classes.statusIcon}>!</div>
            <Typography variant="h5" className={classes.title}>Este enlace ya no es valido.</Typography>
            <Typography variant="body1" color="textSecondary" style={{ marginTop: 12 }}>
              Si necesita una nueva calificacion, solicite otro enlace al asesor.
            </Typography>
          </div>
        </Paper>
      </div>
    );
  }

  if (viewState === "used") {
    return (
      <div className={classes.root}>
        <Paper className={classes.card}>
          <div className={classes.statusWrap}>
            <div className={classes.statusIcon}>✓</div>
            <Typography variant="h4" className={classes.title} gutterBottom>
              Este enlace ya fue utilizado
            </Typography>
            <Typography variant="body1" color="textSecondary">
              La calificacion ya fue registrada anteriormente. Muchas gracias por compartir su opinion.
            </Typography>
          </div>
        </Paper>
      </div>
    );
  }

  if (viewState === "submitted") {
    return (
      <div className={classes.root}>
        <Paper className={classes.card}>
          <div className={classes.statusWrap}>
            <div className={classes.statusIcon}>★</div>
            <Typography variant="h4" className={classes.title} gutterBottom>
              Muchas gracias por su opinion
            </Typography>
            <Typography variant="body1" color="textSecondary" style={{ marginBottom: 12 }}>
              Su calificacion fue registrada correctamente y nos ayuda a mejorar nuestro servicio cada dia.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Puede cerrar esta pagina con tranquilidad.
            </Typography>
          </div>
        </Paper>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.card}>
        <Typography variant="h4" align="center" gutterBottom className={classes.title}>
          Califique su atencion
        </Typography>
        <Typography variant="body1" align="center" color="textSecondary">
          {payload.contactName}, su opinion nos ayuda a mejorar. {payload.agentName ? `Atendido por ${payload.agentName}.` : ""}
        </Typography>

        <div className={classes.stars}>
          {[1, 2, 3, 4, 5].map(value => (
            <button
              key={value}
              type="button"
              className={classes.starButton}
              onClick={() => setStars(value)}
              aria-label={`Calificar con ${value} estrellas`}
            >
              {value <= stars ? "★" : "☆"}
            </button>
          ))}
        </div>

        <TextField
          label="Comentario"
          fullWidth
          multiline
          minRows={4}
          variant="outlined"
          value={comment}
          onChange={e => setComment(e.target.value)}
        />

        <div style={{ marginTop: 24, display: "flex", justifyContent: "center" }}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !stars || submitted}
          >
            {saving ? "Enviando..." : "Enviar calificacion"}
          </Button>
        </div>
      </Paper>
    </div>
  );
}
